import {
  convertMassDisplay,
  formatConvertedAmount,
  isConvertibleMass,
  isSameSystem
} from './weightUnits';

// =====================================================================================
// Ingredient amount formatting.
//
// Amounts are stored as a Number so they can be scaled and fed to the nutrition
// math, but "0.3333333333 cup flour" is not something anyone can cook from.
// Recipes therefore also carry `quantityDisplay` ("1/3"), written by the import
// pipeline, and every surface should prefer it.
//
// Scaling invalidates the stored string, so a scaled amount is re-derived from
// the number instead. Cross-system mass conversion also drops quantityDisplay.
// =====================================================================================

// Fractions worth showing a cook. Anything else falls back to a short decimal.
const COMMON_FRACTIONS = [
  [1, 8], [1, 6], [1, 5], [1, 4], [1, 3], [3, 8], [2, 5], [1, 2],
  [3, 5], [5, 8], [2, 3], [3, 4], [4, 5], [5, 6], [7, 8]
];

const TOLERANCE = 0.021;

/** 0.333 -> "1/3", 1.5 -> "1 1/2", 0.07 -> "0.07". Empty for a missing amount. */
export function toFraction(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '';

  const whole = Math.floor(number + 1e-9);
  const remainder = number - whole;
  if (remainder < 0.02) return String(whole);

  let best = null;
  for (const [numerator, denominator] of COMMON_FRACTIONS) {
    const diff = Math.abs(remainder - numerator / denominator);
    if (diff < TOLERANCE && (!best || diff < best.diff)) {
      best = { numerator, denominator, diff };
    }
  }
  if (!best) return String(Math.round(number * 100) / 100);

  const fraction = `${best.numerator}/${best.denominator}`;
  return whole > 0 ? `${whole} ${fraction}` : fraction;
}

function needsMassConversion(unit, weightUnit) {
  return isConvertibleMass(unit) && !isSameSystem(unit, weightUnit);
}

/**
 * Format an ingredient amount for display.
 *
 * @param {object} ingredient - needs `quantity`, optionally `quantityDisplay`
 * @param {number} scale - serving multiplier; the stored display string only
 *   describes the unscaled amount, so any other scale re-derives from the number
 * @param {string} [weightUnit='lb'] - preferred mass system (`lb` or `kg`)
 */
export function formatQuantity(ingredient, scale = 1, weightUnit = 'lb') {
  return formatIngredientParts(ingredient, scale, weightUnit).amount;
}

/** Amount + display unit after optional mass conversion. */
export function formatIngredientParts(ingredient, scale = 1, weightUnit = 'lb') {
  if (!ingredient) return { amount: '', unit: '' };

  const storedUnit = ingredient.unit && ingredient.unit !== 'whole' ? ingredient.unit : '';
  const quantity = Number(ingredient.quantity);
  const convert = needsMassConversion(storedUnit, weightUnit);

  if (convert && Number.isFinite(quantity) && quantity > 0) {
    const converted = convertMassDisplay(quantity * scale, storedUnit, weightUnit);
    if (converted.converted) {
      return {
        amount: formatConvertedAmount(converted.quantity, converted.unit),
        unit: converted.unit
      };
    }
  }

  if (scale === 1 && ingredient.quantityDisplay) {
    return { amount: String(ingredient.quantityDisplay), unit: storedUnit };
  }

  if (!Number.isFinite(quantity) || quantity <= 0) return { amount: '', unit: storedUnit };
  return { amount: toFraction(quantity * scale), unit: storedUnit };
}

/** "1/3 cup flour, sifted" — the full line as it should read in a list. */
export function formatIngredientLine(ingredient, scale = 1, weightUnit = 'lb') {
  if (!ingredient) return '';
  const { amount, unit } = formatIngredientParts(ingredient, scale, weightUnit);
  const base = [amount, unit, ingredient.name].filter(Boolean).join(' ');
  return ingredient.prepNotes ? `${base}, ${ingredient.prepNotes}` : base;
}
